// src/utils/helpers.js

/**
 * Rewrite a Cloudinary URL to request an auto-format, auto-quality,
 * width-capped version instead of the original upload. Non-Cloudinary
 * URLs are returned unchanged.
 * @param {string} url - The original image URL
 * @param {Object} options - { width } max width to request
 * @returns {string} Optimized image URL
 */
export const getOptimizedImageUrl = (url, { width = 600 } = {}) => {
  if (!url || typeof url !== 'string') return url;

  const uploadMarker = '/upload/';
  const uploadIndex = url.indexOf(uploadMarker);
  if (!url.includes('res.cloudinary.com') || uploadIndex === -1) {
    return url;
  }

  const transformation = `f_auto,q_auto,w_${width},c_limit`;
  const pathAfterUpload = url.slice(uploadIndex + uploadMarker.length);

  // Avoid double-applying if a transformation is already present
  if (/^[a-z]_[^/]+/.test(pathAfterUpload)) {
    return url;
  }

  return `${url.slice(0, uploadIndex + uploadMarker.length)}${transformation}/${pathAfterUpload}`;
};

/**
 * Message to show when a specific store is closed, based on that store's
 * own storeHours ({ openTime: 'HH:MM', closeTime: 'HH:MM' }) rather than a
 * single hardcoded schedule - every store can keep different hours.
 * @param {{openTime?: string, closeTime?: string}} storeHours
 * @returns {string}
 */
export function getStoreClosedMessage(storeHours) {
  const { openTime = '09:00', closeTime = '21:00' } = storeHours || {};

  const now = new Date();
  const [openHour, openMinute] = openTime.split(':').map(Number);
  const [closeHour, closeMinute] = closeTime.split(':').map(Number);

  const open = new Date(now);
  open.setHours(openHour, openMinute, 0, 0);

  const close = new Date(now);
  close.setHours(closeHour, closeMinute, 0, 0);

  if (now < open) {
    return `Store is closed. Please come back at ${openTime}.`;
  }
  if (now >= close) {
    return `Store is closed for today. Please come back tomorrow at ${openTime}.`;
  }
  return 'Store is currently closed.';
}
